import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'

/** Standings and the player's own rank are refreshed on this cadence for as long as a tournament runs. */
const LEADERBOARD_POLL_INTERVAL_MS = 10_000

export class TournamentManager {
    private static _instance: TournamentManager
    private _tournamentId: number | null = null
    private _leaderboardPollId: number | null = null
    private _joinRequested = false
    private _autoOpenHandled = false
    private _baseGameTransitionCompleted = false
    private _pendingBadgeAfterTransition = false
    private _autoOpenPending = false

    private constructor() {
        this.init()
    }

    public static get instance(): TournamentManager {
        if (!TournamentManager._instance) {
            TournamentManager._instance = new TournamentManager()
        }
        return TournamentManager._instance
    }

    private init() {
        // Ascolta il segnale per aprire il torneo e richiedere dati aggiornati
        ;(GameSignals as any).openTournament?.connect(this.onOpenTournament.bind(this))

        // Ascolta il segnale per unirsi al torneo
        ;(GameSignals as any).joinTournament?.connect(this.onJoinTournament.bind(this))

        // Ascolta i dati torneo dal backend
        ;(GameSignals as any).tournamentDataReceived?.connect(this.onTournamentDataReceived.bind(this))

        // Ascolta l'init response per estrarre i dati torneo
        GameSignals.initResponse?.connect(this.onInitResponse.bind(this))

        // Badge torneo deve apparire solo dopo la transition reale intro -> base game.
        // completeSplashScreenState scatta al click splash (troppo presto), quindi usiamo
        // il segnale di transition completion e il fallback base background.
        GameSignals.completeTransitionAnimation?.connect(this.onBaseGameTransitionCompleted)
        GameSignals.showBaseGameBackground?.connect(this.onBaseGameTransitionCompleted)

        console.log('[TournamentManager] Initialized')
    }

    private onBaseGameTransitionCompleted = () => {
        if (this._baseGameTransitionCompleted) return

        this._baseGameTransitionCompleted = true
        console.log('[TournamentManager] Base game transition completed')

        if (this._pendingBadgeAfterTransition) {
            this._pendingBadgeAfterTransition = false
            this.showBadgeOnly()
        }

        this.runPendingAutoOpen()
    }

    /**
     * The first tournament open is owned by this manager, not by the splash components: the only
     * other emitter of openTournament lives inside a spine item whose asset does not exist in this
     * game, so its events are never registered and the signal never fires.
     */
    private runPendingAutoOpen() {
        if (!this._autoOpenPending || this._autoOpenHandled) {
            return
        }

        this._autoOpenPending = false
        this.onOpenTournament()
    }

    private onOpenTournament() {
        console.log('[TournamentManager] Opening tournament')
        
        const model = (Facade as any).model
        const tournamentData = model?.tournamentData || model?.initData?.tournament_data

        if (!tournamentData) {
            console.warn('[TournamentManager] No tournament data available')
            return
        }

        this._tournamentId = tournamentData.tournament_id

        const isPlayerJoined = Number(tournamentData?.is_player_joined || 0) === 1

        // Legacy behavior: first automatic open after splash should show only the trophy badge
        // when the player already joined the tournament.
        if (isPlayerJoined && !this._autoOpenHandled) {
            this._autoOpenHandled = true

            if (!this._baseGameTransitionCompleted) {
                this._pendingBadgeAfterTransition = true
                ;(Facade as any)._showTournament = false
                ;(Facade as any)._showTournamentBadge = false
                return
            }

            this.showBadgeOnly()
            return
        }

        this._autoOpenHandled = true

        // Do not call tournament PHP on popup open.
        // Backend request must happen only when user clicks Join.

        // Apri il popup del torneo
        this.open()
    }

    private showBadgeOnly() {
        console.log('[TournamentManager] Showing tournament badge only')

        if (!this._baseGameTransitionCompleted) {
            this._pendingBadgeAfterTransition = true
            ;(Facade as any)._showTournament = false
            ;(Facade as any)._showTournamentBadge = false
            return
        }

        ;(Facade as any)._showTournament = false
        ;(Facade as any)._showTournamentBadge = true

        const adapter = (window as any).__cashCongExternalUiAdapter
        if (adapter && typeof adapter.pushSnapshot === 'function') {
            adapter.pushSnapshot()
        }
    }

    private open() {
        console.log('[TournamentManager] Setting showTournament to true')
        ;(Facade as any)._showTournament = true
        ;(Facade as any)._showTournamentBadge = false
        
        // Aggiorna lo snapshot per notificare la UI
        const adapter = (window as any).__cashCongExternalUiAdapter
        if (adapter && typeof adapter.pushSnapshot === 'function') {
            adapter.pushSnapshot()
        }
    }

    public openFromBadge() {
        // Explicit user action from badge must always open popup.
        this._autoOpenHandled = true
        this.open()
    }

    /**
     * The trophy belongs to an entrant, and only once the base game is on screen — before that it is
     * queued and raised by the transition handler.
     */
    private syncBadgeVisibility() {
        if (!this.isPlayerJoined()) {
            this._pendingBadgeAfterTransition = false
            ;(Facade as any)._showTournamentBadge = false
            return
        }

        if (this._baseGameTransitionCompleted) {
            ;(Facade as any)._showTournamentBadge = true
        } else {
            this._pendingBadgeAfterTransition = true
            ;(Facade as any)._showTournamentBadge = false
        }
    }

    public close() {
        console.log('[TournamentManager] Setting showTournament to false')
        ;(Facade as any)._showTournament = false

        this.syncBadgeVisibility()

        // Aggiorna lo snapshot per notificare la UI
        const adapter = (window as any).__cashCongExternalUiAdapter
        if (adapter && typeof adapter.pushSnapshot === 'function') {
            adapter.pushSnapshot()
        }
    }

    private onInitResponse(data: any) {
        console.log('[TournamentManager] Init response received', data)

        // Estrai e salva i dati torneo se presenti
        if (data?.tournament_data) {
            const model = (Facade as any).model
            if (model) {
                model.tournamentData = data.tournament_data
                this._tournamentId = data.tournament_data.tournament_id
            }

            this.syncLeaderboardPolling()
            this.syncBadgeVisibility()

            console.log('[TournamentManager] Tournament data found:', data.tournament_data)

            // Trigger snapshot update nell'ExternalUiAdapter
            ;(GameSignals as any).backendMessage?.emit({ action: 'tournament_data_updated' })

            this._autoOpenPending = true

            // Restore flows can hand the base game over before the init response is parsed, so the
            // auto-open has to fire here too when the transition signal already came and went.
            if (this._baseGameTransitionCompleted) {
                this.runPendingAutoOpen()
            }
        }
    }

    private onTournamentDataReceived(data: any) {
        console.log('[TournamentManager] Tournament data updated:', data)

        // Aggiorna il model
        const model = (Facade as any).model
        if (model && data) {
            // Merge i nuovi dati con quelli esistenti
            model.tournamentData = {
                ...model.tournamentData,
                ...data,
            }

            if (!this._tournamentId) {
                this._tournamentId = model.tournamentData?.tournament_id || null
            }

            // The acknowledgement of a join the player just made. The endpoint answers the same
            // payload for a refresh as for a join, so it is this request, not the response, that
            // says the player is in — and some responses come back without the flag at all.
            if (this._joinRequested) {
                this._joinRequested = false

                if (model.tournamentData) {
                    model.tournamentData.is_player_joined = 1
                }
            }
        }

        this.syncLeaderboardPolling()

        // The join closes the popup before its response lands, and close() reads a joined flag that
        // is still 0 at that point and puts the badge away. Nothing raised it again until the next
        // init, which is why the trophy only turned up after a reload.
        this.syncBadgeVisibility()

        // Trigger snapshot update
        ;(GameSignals as any).backendMessage?.emit({ action: 'tournament_data_updated' })
    }

    private onJoinTournament(tournamentId: number) {
        const model = (Facade as any).model
        const fallbackTournamentId = this._tournamentId || model?.tournamentData?.tournament_id || model?.initData?.tournament_data?.tournament_id
        const resolvedTournamentId = Number(tournamentId || fallbackTournamentId || 0)

        console.log('[TournamentManager] Joining tournament:', {
            requestedTournamentId: tournamentId,
            fallbackTournamentId,
            resolvedTournamentId,
        })

        if (!resolvedTournamentId) {
            console.warn('[TournamentManager] Join skipped: missing tournament id')
            return
        }

        this._joinRequested = true

        const backendManager = (Facade as any).backendManager
        if (backendManager && typeof backendManager.joinTournament === 'function') {
            backendManager.joinTournament(resolvedTournamentId)
        }
    }

    private requestLeaderboard(tournamentId: number) {
        console.log('[TournamentManager] Requesting leaderboard for tournament:', tournamentId)
        
        const backendManager = (Facade as any).backendManager
        if (backendManager && typeof backendManager.getTournamentLeaderboard === 'function') {
            backendManager.getTournamentLeaderboard(tournamentId)
        }
    }

    public refreshLeaderboard() {
        if (this._tournamentId) {
            this.requestLeaderboard(this._tournamentId)
        }
    }

    private isPlayerJoined(): boolean {
        const model = (Facade as any).model
        const tournamentData = model?.tournamentData || model?.initData?.tournament_data
        return Number(tournamentData?.is_player_joined || 0) === 1
    }

    /**
     * Only an entrant has standings to follow, so the poll is tied to having joined rather than to
     * the tournament merely existing. Called on every tournament payload, which is also how joining
     * turns it on: the join response is what flips is_player_joined.
     */
    private syncLeaderboardPolling() {
        if (this._tournamentId && this.isPlayerJoined()) {
            this.startLeaderboardPolling()
        } else {
            this.stopLeaderboardPolling()
        }
    }

    /**
     * The endpoint it hits is resolved per launch by ServerRequests, so it follows whichever domain
     * served the game.
     */
    private startLeaderboardPolling() {
        if (this._leaderboardPollId !== null) {
            return
        }

        this._leaderboardPollId = window.setInterval(() => this.pollLeaderboard(), LEADERBOARD_POLL_INTERVAL_MS)
        window.addEventListener('pagehide', this.stopLeaderboardPolling)
        console.log('[TournamentManager] Leaderboard polling every', LEADERBOARD_POLL_INTERVAL_MS, 'ms')
    }

    public stopLeaderboardPolling = () => {
        if (this._leaderboardPollId === null) {
            return
        }

        window.clearInterval(this._leaderboardPollId)
        this._leaderboardPollId = null
    }

    private pollLeaderboard() {
        // A hidden tab shows no standings, and its timers are throttled anyway, so the tick would
        // only spend a request to update something nobody is looking at.
        if (!this._tournamentId || document.hidden) {
            return
        }

        const backendManager = (Facade as any).backendManager
        if (backendManager && typeof backendManager.refreshTournamentLeaderboardInBackground === 'function') {
            backendManager.refreshTournamentLeaderboardInBackground(this._tournamentId)
        }
    }
}

// Inizializza il singleton
TournamentManager.instance

// Esponi metodo globale per testing da console
;(window as any).openTournament = () => {
    console.log('[Global] Opening tournament from console command')
    ;(GameSignals as any).openTournament?.emit()
}

;(window as any).closeTournament = () => {
    console.log('[Global] Closing tournament from console command')
    TournamentManager.instance.close()
}
